import { INSURANCE_PROVIDER_NAMES } from '@shipstation/client-common/constants/insurance';
import { useSystemAutomation } from '../contexts/system-automation-context';
import { SYSTEM_RULE_NAMES } from '../contexts/system-automation-context/constants';
import { SellerSettings } from '../enums/seller-settings/seller-settings';
import { updateRule } from '../pages/settings/api/automation-rules';
import { updateSellerSettingsBulk } from '../pages/settings/api/save-settings';
import { getAutoInsureSettingKey, useShouldAutoInsure, useWalletedInsuranceProviderId } from '../services/insurance-service';
import { useSellerSetting } from './use-seller-setting';

// not ideal perhaps, but this is a simple way to expose
// a value only accessible via hooks to analytics code that needs it
// deep inside epics. Consider a refactor if this becomes more widely used.
export type InsurancePreferenceCache = {
  insurancePreference: string;
};

export const insurancePreferenceCache = {
  insurancePreferenceForAnalytics: '',
};

const setPreferenceCache = (newCache: string) => {
  insurancePreferenceCache.insurancePreferenceForAnalytics = newCache;
};

export const getInsurancePreferenceForAnalytics = () => {
  return insurancePreferenceCache.insurancePreferenceForAnalytics;
};

export const INSURANCE_PREFERENCE = {
  INSURE_ALL: 'insure_all',
  INSURE_BY_RULES: 'insure_rules',
  NONE: 'none',
  UNDECIDED: 'undecided',
} as const;

export const getInsurancePreferenceDisplayName = (preference: string, insuranceProviderName?: string): string => {
  const providerName = insuranceProviderName ?? INSURANCE_PROVIDER_NAMES.PARCELGUARD;

  switch (preference) {
    case INSURANCE_PREFERENCE.INSURE_ALL:
      return `Insure All with ${providerName}`;
    case INSURANCE_PREFERENCE.INSURE_BY_RULES:
      return `Insure by Rules with ${providerName}`;
    case INSURANCE_PREFERENCE.NONE:
      return 'Do not insure all';
    case INSURANCE_PREFERENCE.UNDECIDED:
      return 'Undecided';
    default:
      return 'Unknown';
  }
};

export type InsurancePreference = (typeof INSURANCE_PREFERENCE)[keyof typeof INSURANCE_PREFERENCE];

export const deriveInsurancePreference = (
  shouldAutoInsure: boolean,
  hasActiveInsuranceRule: boolean,
  hasInsureByRulesPreference: boolean,
): InsurancePreference => {
  if (shouldAutoInsure) return INSURANCE_PREFERENCE.INSURE_ALL;
  if (hasActiveInsuranceRule || hasInsureByRulesPreference) return INSURANCE_PREFERENCE.INSURE_BY_RULES;
  return INSURANCE_PREFERENCE.NONE;
};

export const useInsurancePreference = () => {
  const insuranceProviderId = useWalletedInsuranceProviderId();
  const { shouldAutoInsure } = useShouldAutoInsure(insuranceProviderId);
  const { getSystemRule, isLoading } = useSystemAutomation();

  const preferenceInsureByRules = useSellerSetting(SellerSettings.insuranceSystemAutomationPreferenceInsureByRules);
  const hasInsureByRulesPreference = preferenceInsureByRules === 'true';

  // Distinguishes "user explicitly selected None and saved" from "user has made no choice yet".
  // Both derive to NONE, but only the former should block the auto-enable default.
  const preferenceNone = useSellerSetting(SellerSettings.insuranceSystemAutomationPreferenceNone);
  const hasExplicitNonePreference = preferenceNone === 'true';

  const existingRule = getSystemRule(SYSTEM_RULE_NAMES.INSURE_BY_RULES);
  const hasActiveInsuranceRule = existingRule?.active ?? false;
  const insurancePreference = deriveInsurancePreference(shouldAutoInsure, hasActiveInsuranceRule, hasInsureByRulesPreference);

  const insurancePreferenceForAnalytics =
    !hasExplicitNonePreference && insurancePreference === INSURANCE_PREFERENCE.NONE ? INSURANCE_PREFERENCE.UNDECIDED : insurancePreference;
  setPreferenceCache(insurancePreferenceForAnalytics);

  return {
    insurancePreference,
    isLoading,
    hasActiveInsuranceRule,
    hasExplicitNonePreference,
    insurancePreferenceForAnalytics,
  };
};

type SystemRule = {
  automationRuleId?: string;
  active?: boolean;
  [key: string]: unknown;
};

type SaveInsurancePreferenceArgs = {
  preference: InsurancePreference;
  insuranceProviderId: string;
  existingSystemRule: SystemRule | null | undefined;
  refreshAutomationRules?: () => Promise<unknown>;
  // Settings-only: keeps the legacy preferenceNone seller setting in sync; not read by deriveInsurancePreference.
  shouldWritePreferenceNone?: boolean;
};

export const saveInsurancePreference = async ({
  preference,
  insuranceProviderId,
  existingSystemRule,
  refreshAutomationRules,
  shouldWritePreferenceNone = false,
}: SaveInsurancePreferenceArgs) => {
  const autoInsureKey = getAutoInsureSettingKey(insuranceProviderId);
  if (!autoInsureKey) {
    throw new Error(`Unknown insurance provider id: ${insuranceProviderId}`);
  }
  const settings: Record<string, string> = {
    [autoInsureKey]: preference === INSURANCE_PREFERENCE.INSURE_ALL ? 'true' : 'false',
    [SellerSettings.insuranceSystemAutomationPreferenceInsureByRules.key]:
      preference === INSURANCE_PREFERENCE.INSURE_BY_RULES ? 'true' : 'false',
  };
  if (shouldWritePreferenceNone) {
    settings[SellerSettings.insuranceSystemAutomationPreferenceNone.key] = preference === INSURANCE_PREFERENCE.NONE ? 'true' : 'false';
  }

  const shouldDeactivateRule = Boolean(existingSystemRule?.active) && preference !== INSURANCE_PREFERENCE.INSURE_BY_RULES;

  const [newSettings] = await Promise.all([
    updateSellerSettingsBulk(settings),
    shouldDeactivateRule ? updateRule({ ...existingSystemRule, active: false }) : Promise.resolve(null),
  ]);

  if (shouldDeactivateRule && refreshAutomationRules) {
    await refreshAutomationRules();
  }
  return newSettings;
};
